import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { defaultDatePickerStrings } from "@fluentui/react-datepicker-compat";
import { useToastGlobal } from "../../../stores/useToastGlobal";
import {
    useQueryGetAllAgencies,
    useQueryGetAllManagers,
    useQueryGetAllServices,
} from "../../../api/helpers/queries";
import { _getAllUsers } from "../../../api/user";
import {
    _api_export_pilotage_detail,
    _api_export_pilotage_synthese,
} from "../api";
import {
    buildExportDetailSearchParams,
    buildExportSyntheseSearchParams,
} from "../api/queries";
import { EXPORT_DETAIL_GROUPS, EXPORT_SYNTHESE_OPTIONS } from "../constants";

const COMMON_OPTIONS = { returnObjects: true, ns: "common" };

export const EXPORT_TABS = {
    synthese: "synthese",
    detail: "detail",
};

const buildDetailState = () =>
    EXPORT_DETAIL_GROUPS.reduce((acc, group) => {
        group.options.forEach((opt) => {
            acc[opt.key] = !!opt.checked;
        });
        return acc;
    }, {});

const buildDefaultValues = () => ({
    start_date: new Date(new Date().getFullYear(), 0, 1),
    end_date: new Date(),
    export_format: "xlsx",
    department_ids: [],
    agency_ids: [],
    manager_ids: [],
    compare_by: "department",
    sections: Object.fromEntries(
        EXPORT_SYNTHESE_OPTIONS.map((opt) => [opt.key, true])
    ),
});

const toItems = (list, getHeader) =>
    (list ?? []).map((item) => ({ key: item.id, header: getHeader(item) }));

const pad = (n) => String(n).padStart(2, "0");
export const formatDate = (d) =>
    d ? `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}` : "";

/** Logique de la modale "Exporter les données" */
const useExportDialog = () => {
    const { t } = useTranslation();
    const setShowToast = useToastGlobal((s) => s.setShow);

    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState(EXPORT_TABS.synthese);
    const [isExporting, setIsExporting] = useState(false);

    // période, format, périmètre & sections de la synthèse
    const form = useForm({ defaultValues: buildDefaultValues() });
    const [startDate, endDate, sections] = form.watch([
        "start_date",
        "end_date",
        "sections",
    ]);

    // options du périmètre (mêmes queries que la FilterBar)
    const services = useQueryGetAllServices();
    const agencies = useQueryGetAllAgencies();
    const managers = useQueryGetAllManagers();

    // onglet détail : liste des collaborateurs (chargée à l'ouverture de la modale)
    const users = useQuery(["pilotage_rh_export_users"], () => _getAllUsers(), {
        enabled: open,
        refetchOnWindowFocus: false,
    });
    const [collaborateurs, setCollaborateurs] = useState([]);
    const [detail, setDetail] = useState(buildDetailState);

    const localizedStrings = useMemo(
        () => ({
            ...defaultDatePickerStrings,
            days: t("temporal.days", COMMON_OPTIONS),
            shortDays: t("temporal.shortDays", COMMON_OPTIONS),
            months: t("temporal.months", COMMON_OPTIONS),
            shortMonths: t("temporal.shortMonths", COMMON_OPTIONS),
            goToToday: t("temporal.goToToday", COMMON_OPTIONS),
        }),
        [t]
    );

    const collaborateursDisponibles = useMemo(
        () =>
            (Array.isArray(users.data) ? users.data : []).map((u) => ({
                key: u.id,
                header: u.name,
                content: u.job_title,
                image: u.avatar,
            })),
        [users.data]
    );

    const toggleDetail = (key) =>
        setDetail((prev) => ({ ...prev, [key]: !prev[key] }));

    const isGroupAllChecked = (group) =>
        group.options.every((opt) => detail[opt.key]);

    const toggleGroup = (group) => {
        const all = isGroupAllChecked(group);
        setDetail((prev) => {
            const next = { ...prev };
            group.options.forEach((opt) => {
                next[opt.key] = !all;
            });
            return next;
        });
    };

    const reset = () => {
        setTab(EXPORT_TABS.synthese);
        form.reset(buildDefaultValues());
        setCollaborateurs([]);
        setDetail(buildDetailState());
    };

    const close = () => {
        setOpen(false);
        reset();
    };

    const selectedColumns = Object.keys(detail).filter((key) => detail[key]);

    const canExport =
        !!startDate &&
        !!endDate &&
        !isExporting &&
        (tab === EXPORT_TABS.synthese
            ? Object.values(sections).some(Boolean)
            : selectedColumns.length > 0);

    const exportData = form.handleSubmit((values) => {
        setIsExporting(true);
        const isDetail = tab === EXPORT_TABS.detail;
        const exportFn = isDetail
            ? _api_export_pilotage_detail
            : _api_export_pilotage_synthese;
        return exportFn({
            searchParams: isDetail
                ? buildExportDetailSearchParams(values, {
                      employeeIds: collaborateurs,
                      fields: selectedColumns,
                  })
                : buildExportSyntheseSearchParams(values),
            exportFormat: values.export_format,
            cbOnEndDownload: (data, error, message) => {
                setIsExporting(false);
                if (error) {
                    setShowToast({
                        visible: true,
                        level: "danger",
                        content:
                            message ||
                            "Une erreur s'est produite lors de l'export",
                    });
                    return;
                }
                setShowToast({
                    visible: true,
                    level: "success",
                    content: "Export téléchargé",
                    auto_hide_timeout: 2400,
                });
                close();
            },
        });
    });

    return {
        dialog: { open, openDialog: () => setOpen(true), close },
        tab: { value: tab, set: setTab },
        form,
        periode: { localizedStrings, formatDate },
        perimetre: {
            departements: {
                items: toItems(services.data, (s) => s.name),
                isLoading: services.isLoading,
            },
            agences: {
                items: toItems(agencies.data, (a) => a.name),
                isLoading: agencies.isLoading,
            },
            managers: {
                items: toItems(
                    managers.data,
                    (m) => `${m.first_name} ${m.last_name}`
                ),
                isLoading: managers.isLoading,
            },
            collaborateurs,
            setCollaborateurs,
            collaborateursDisponibles,
            isLoadingCollaborateurs: users.isLoading,
        },
        colonnes: {
            detail,
            toggleDetail,
            isGroupAllChecked,
            toggleGroup,
        },
        exportData,
        canExport,
        isExporting,
    };
};

export default useExportDialog;
